import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Chip, formatNumber } from '@column/column-ui-kit';

import { Balance, FormLabelAmount, FormTextAmount } from '~/app/pages/BankAccounts/Edit';
import { ROUTE } from '~/app/routes';
import { BankAccountCard, CardProgramCard, CopyInput, LoanCard, Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { CardAccountRepository } from '~/repositories/CardAccountRepository';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { LoanRepository } from '~/repositories/LoanRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, FormElement, FormLabel, FormText, Inner } from '~/styles';
import { formatDateTime, formatString } from '~/util';

import { CardAccountProps } from './CardAccount';

interface Params {
  id: string;
}

const parseLoanBalance = (value?: string): number => {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

const isCreditLikeProgramType = (cardProgramType?: string): boolean => {
  if (!cardProgramType) return false;
  const normalizedType = cardProgramType.toLowerCase();
  return normalizedType === 'credit' || normalizedType === 'charge';
};

export const PageCardAccountInformation: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { cardAccount: outletCardAccount } = useOutletContext<CardAccountProps>();
  const { currentPlatform, isSandbox } = useSessionStore();
  const [cardProgram, setCardProgram] = useState<CardProgram | null>(null);

  const { data: cardAccount, error: cardAccountError } = useQuery({
    queryKey: ['card-account', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardAccountRepository.get(id!),
    enabled: !!id && !outletCardAccount,
    initialData: outletCardAccount,
  });

  const { data: bankAccount } = useQuery({
    queryKey: ['card-account-bank-account', cardAccount?.bankAccountId, currentPlatform?.id, isSandbox],
    queryFn: () => BankAccountRepository.get({ id: cardAccount!.bankAccountId! }),
    enabled: Boolean(cardAccount?.bankAccountId),
  });

  const { data: loan } = useQuery({
    queryKey: ['card-account-loan', cardAccount?.loanId, currentPlatform?.id, isSandbox],
    queryFn: () => LoanRepository.get(cardAccount!.loanId!),
    enabled: Boolean(cardAccount?.loanId),
  });

  useEffect(() => {
    const fetchCardProgram = async () => {
      if (!cardAccount?.cardProgramId) return;
      try {
        const response = await CardProgramRepository.listCardPrograms();
        const program = response.cardPrograms?.find((entry) => entry.id === cardAccount.cardProgramId);
        if (program) {
          setCardProgram(program);
        }
      } catch (err) {
        console.error('Error fetching card program:', err);
      }
    };
    fetchCardProgram();
  }, [cardAccount?.cardProgramId]);

  useEffect(() => {
    if (cardAccountError) {
      navigate(ROUTE.CARD_ACCOUNTS);
      addDangerNotification({
        content: (cardAccountError as any).message || 'Failed to load card account',
      });
    }
  }, [cardAccountError, navigate, addDangerNotification]);

  if (!cardAccount) {
    return null;
  }

  const cardProgramType = cardProgram?.cardProgramType?.toLowerCase();

  const renderBankBalanceGrid = () => {
    if (!bankAccount?.balances) {
      return (
        <FormText>
          <Flex>Balance information not available for this linked bank account.</Flex>
        </FormText>
      );
    }

    const available = bankAccount.balances.availableAmount ?? 0;
    const held =
      (bankAccount.balances.pendingAmount ?? 0) +
      (bankAccount.balances.lockedAmount ?? 0) +
      (bankAccount.balances.holdingAmount ?? 0);
    const total = available + held;

    return (
      <Balance>
        <Flex flexDirection="column" gap={4}>
          <FormTextAmount isNegative={available < 0} isAvailable>
            {formatNumber(available)}
          </FormTextAmount>
          <FormLabelAmount>Available</FormLabelAmount>
        </Flex>
        <Flex flexDirection="column" gap={4}>
          <FormTextAmount isNegative={held < 0}>{formatNumber(held)}</FormTextAmount>
          <FormLabelAmount>Held</FormLabelAmount>
        </Flex>
        <Flex flexDirection="column" gap={4}>
          <FormTextAmount isNegative={total < 0}>{formatNumber(total)}</FormTextAmount>
          <FormLabelAmount>Total</FormLabelAmount>
        </Flex>
      </Balance>
    );
  };

  const renderLoanBalanceGrid = () => {
    if (!loan?.balances) {
      return (
        <FormText>
          <Flex>Balance information not available for this linked loan.</Flex>
        </FormText>
      );
    }

    const maxPrincipalBalance = parseLoanBalance(loan.maxPrincipalBalance);
    const held = parseLoanBalance(loan.balances.principalHolding);
    const spent = parseLoanBalance(loan.balances.principalOutstanding);
    const available = clamp(maxPrincipalBalance - held - spent, 0, maxPrincipalBalance);
    const total = maxPrincipalBalance;
    const currencyCode = loan.currency || 'USD';

    return (
      <Balance>
        <Flex flexDirection="column" gap={4}>
          <FormTextAmount isNegative={available < 0} isAvailable>
            {formatNumber(available, currencyCode)}
          </FormTextAmount>
          <FormLabelAmount>Available</FormLabelAmount>
        </Flex>
        <Flex flexDirection="column" gap={4}>
          <FormTextAmount isNegative={held < 0}>{formatNumber(held, currencyCode)}</FormTextAmount>
          <FormLabelAmount>Held</FormLabelAmount>
        </Flex>
        <Flex flexDirection="column" gap={4}>
          <FormTextAmount isNegative={spent < 0}>{formatNumber(spent, currencyCode)}</FormTextAmount>
          <FormLabelAmount>Spent</FormLabelAmount>
        </Flex>
        <Flex flexDirection="column" gap={4}>
          <FormTextAmount isNegative={total < 0}>{formatNumber(total, currencyCode)}</FormTextAmount>
          <FormLabelAmount>Total</FormLabelAmount>
        </Flex>
      </Balance>
    );
  };

  const renderBalanceSection = () => {
    if (cardProgramType === 'debit') {
      return renderBankBalanceGrid();
    }

    if (isCreditLikeProgramType(cardProgramType)) {
      return renderLoanBalanceGrid();
    }

    if (cardAccount.bankAccountId && bankAccount?.balances) {
      return renderBankBalanceGrid();
    }

    if (cardAccount.loanId && loan) {
      return renderLoanBalanceGrid();
    }

    return (
      <FormText>
        <Flex>Balance information not available.</Flex>
      </FormText>
    );
  };

  return (
    <Inner p={0}>
      <Section headline="Balance">
        <Inner px="var(--page-margin)" py={16}>
          {renderBalanceSection()}
        </Inner>
      </Section>
      <Section headline="Information">
        <Inner px="var(--page-margin)" py={16}>
          <Flex flexDirection="column" gap={16}>
            <FormElement>
              <FormLabel>Card Account ID</FormLabel>
              <CopyInput value={cardAccount.id} isMonospaced />
            </FormElement>

            <FormElement>
              <FormLabel>Card Program</FormLabel>
              {cardAccount.cardProgramId ? (
                <CardProgramCard cardProgramId={cardAccount.cardProgramId} />
              ) : (
                <FormText>{cardProgram?.description || '-'}</FormText>
              )}
            </FormElement>

            <FormElement>
              <FormLabel>Status</FormLabel>
              <FormText>
                <Chip>{formatString(cardAccount.status || '')}</Chip>
              </FormText>
            </FormElement>

            <FormElement>
              <FormLabel>Bank Account</FormLabel>
              {cardAccount.bankAccountId ? (
                <BankAccountCard bankAccountId={cardAccount.bankAccountId} />
              ) : (
                <FormText>-</FormText>
              )}
            </FormElement>

            <FormElement>
              <FormLabel>Loan</FormLabel>
              {cardAccount.loanId ? <LoanCard loanId={cardAccount.loanId} /> : <FormText>-</FormText>}
            </FormElement>

            {cardAccount.createdAt && (
              <FormElement>
                <FormLabel>Created At</FormLabel>
                <CopyInput value={formatDateTime(cardAccount.createdAt, { timeZoneFormat: 'utc' })} />
              </FormElement>
            )}
          </Flex>
        </Inner>
      </Section>
    </Inner>
  );
};
